<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <v-form ref="fireSimulationForm" v-model="isValid">
        <card-title-row translation-path="sidebar.fireSimulation.title" class="pb-3" :has-buttons="false" />
        <sidebar-row border-bottom class="pb-3">
          <v-row no-gutters>
            <v-col cols="7">
              <data-input v-bind="waterProfilesConfig" v-model="modelingProfile" />
            </v-col>
          </v-row>
          <v-row no-gutters class="pb-5">
            <v-col>
              <italic-subheader
                style="font-size: 12px !important;"
                class="text-left"
                translation-path="sidebar.fireSimulation.modelingProfileSubtitle"
              />
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-2">
          <v-row no-gutters style="background-color: white">
            <v-col cols="auto" class="text-left">
              <v-icon color="primary" class="mr-2" size="24">mdi-information</v-icon>
            </v-col>
            <v-col cols="10" class="text-left fire-simulation-info" :style="`color: ${this.$_colors.primary}`">
              {{ $i18n.t('sidebar.fireSimulation.titleInfo') }}
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-5">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="5" class="text-body-2 center-content text-left pt-4">
              <v-row no-gutters> {{ $i18n.t('sidebar.fireSimulation.waterConsumption') }}: </v-row>
              <v-row no-gutters>
                <v-col>
                  <italic-subheader
                    style="font-size: 12px !important;"
                    class="text-left"
                    :straight-text="settingsValuesUnitText"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row>
                <v-col cols="12">
                  <v-radio-group
                    class="my-0 py-0 input--type"
                    v-model="waterConsumptionType"
                    :rules="waterConsumptionRules"
                    dense
                    row
                    hide-details
                    @change="waterConsumptionValue = ''"
                  >
                    <v-radio :label="$i18n.t('dialog.value')" value="value"></v-radio>
                    <v-radio :label="$i18n.t('dialog.attribute')" value="attribute"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters style="width: 100%">
                <v-col>
                  <data-input
                    v-bind="waterConsumptionConfig"
                    :height="20"
                    v-model="waterConsumptionValue"
                    :rules="waterConsumptionRules"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pt-6">
          <v-row no-gutters class="justify-space-between align-baseline">
            <v-col cols="5" class="text-body-2 center-content text-left">
              <v-row no-gutters> {{ $i18n.t('sidebar.quickCheck.pattern') }}: </v-row>
            </v-col>
            <v-col cols="6">
              <data-input v-bind="patternsConfig" v-model="selectedPattern" />
            </v-col>
          </v-row>
        </sidebar-row>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import SidebarRow from '@/components/SidebarRow';

import profiles from '@/mixins/profiles';
import modelingOptionsValues from '@/mixins/modelingOptionsValues';
import getBbox from '@/mixins/map/getBbox';

export default {
  name: 'FireSimulation',
  mixins: [profiles, modelingOptionsValues, getBbox],
  components: { CardTitleRow, DataInput, ItalicSubheader, SidebarRow },
  computed: {
    epanetProfiles: get('epanet/epanetProfiles'),
    layers: get('layers/layers'),
    patterns: get('epanet/patterns'),
    hydrantsRoleLayerSelectedFeatures: get('map/selectedFeatures@:hydrantsRoleLayerId'),
    hydrantsSelectedFeatures() {
      return this.hydrantsRoleLayerSelectedFeatures || [];
    },
    hydrantsAttributes() {
      return this.$_flattenStructure(this.hydrantsRoleLayer.form_schema.elements, 'elements');
    },
    hydrantsRoleLayer() {
      return this.$_objectToArray(this.layers).find(layer => layer.role === 'water_hydrants');
    },
    hydrantsRoleLayerId() {
      return this.hydrantsRoleLayer?.id;
    },
    patternsConfig() {
      return {
        items: this.patterns,
        dataType: { name: 'select' },
        'item-value': 'name',
        'item-text': 'name',
        rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
      };
    },
    waterConsumptionConfig() {
      return this.waterConsumptionType === 'attribute'
        ? {
            itemText: 'label',
            itemValue: 'attribute',
            items: this.hydrantsAttributes,
            label: this.$i18n.t('dialog.body.attribute'),
            dataType: { name: 'select' },
          }
        : {
            label: this.$i18n.t('dialog.body.value'),
            dataType: { name: 'float' },
          };
    },
    waterConsumptionRules() {
      return [v => !!v || v === false || v === 0 || this.$i18n.t('rules.required')];
    },
    waterProfilesConfig() {
      return {
        label: this.$i18n.t('sidebar.fireSimulation.modelingProfile'),
        dataType: { name: 'select' },
        items: this.epanetProfiles,
        itemText: 'name',
        returnObject: true,
        rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
        hideDetails: true,
      };
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),

    settingsValues: get('admin/settingsValues.epanet_options'),
    settingsValuesUnit() {
      return this.settingsValues?.['UNITS'] || '';
    },
    settingsValuesUnitText() {
      return `${this.$i18n.t('dialog.body.measurementUnit')}: ${this.settingsValuesUnits[this.settingsValuesUnit] ||
        ''}`;
    },
    settingsValuesUnits() {
      const settingsValuesUnits = {};
      this.unitsValues.forEach(unit => {
        const text = this.$i18n.t(`dialog.body.modelingOptions.selectValues.UNITS.${unit}`);
        settingsValuesUnits[unit] = this.$_loweralize(text);
      });
      return settingsValuesUnits;
    },
  },
  data: () => ({
    isFetched: false,
    isValid: false,
    waterConsumptionType: 'value',
    waterConsumptionValue: '',
    modelingProfile: undefined,
    selectedPattern: null,
  }),
  methods: {
    getEpanetProfiles: call('epanet/getEpanetProfiles'),
    runModel: call('epanet/runModel'),
    getPatterns: call('epanet/getPatterns'),
    async getPayload() {
      const bbox = await this.getFeaturesBbox({
        layerId: this.hydrantsRoleLayerId,
        featuresIds: this.hydrantsSelectedFeatures,
      });
      const modelingProfile = this.getWaterModelingProfilePayload(this.modelingProfile);
      const hydrantsDemand = {
        hydrants: this.hydrantsSelectedFeatures,
        [this.waterConsumptionType]: this.waterConsumptionValue,
      };
      const payload = {
        ...modelingProfile,
        hydrants_demand: {
          ...hydrantsDemand,
          pattern_name: this.selectedPattern,
        },
        bbox,
      };
      return payload;
    },
    async init() {
      this.isFetched = false;
      try {
        await Promise.all([this.getEpanetProfiles(), this.getPatterns()]);
        this.$root.$off('runFireSimulation');
        this.$root.$on('runFireSimulation', this.runSimulation);
        this.stickyButtons = [
          {
            action: 'runFireSimulation',
            translationPath: 'dialog.agreeRun',
          },
        ];
        this.isStickyButtonsLoading = false;
        this.isStickyButtonsVisible = true;
        this.isFetched = true;
      } catch {
        //
      }
    },
    async runSimulation() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = await this.getPayload();
        const r = await this.runModel(payload);
        this.$router.push({
          name: 'modelingResultsScenario',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { formValid: vm.isValid, selectedFeatures: vm.hydrantsSelectedFeatures };
      },
      nV => {
        const { formValid, selectedFeatures } = nV;
        this.isStickyButtonsValid = formValid && selectedFeatures?.length ? true : false;
      }
    );
    this.init();
  },
  beforeDestroy() {
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
.fire-simulation-info {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
}
::v-deep {
  .input--type {
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
}
</style>
